import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import HeroAnimation from "../components/HeroAnimation";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { PRODUCTS } from "../data/products";
import { Star, ShieldCheck, Truck, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVIEWS = [
  { quote: "Wrapped in comfort and good vibes. Thank you for this amazing hoodie — absolutely loved it!", author: "Verified Customer", location: "India" },
  { quote: "This hoodie is a whole vibe! Quality is top-notch, print is amazing, and the design is super stylish. Loved it!", author: "@jagritahuja_", location: "Verified Customer, India" },
  { quote: "Quality of the hoodie is very good, shipment was fast, COD was available, and the Porsche 911 hoodie design is very classic.", author: "Verified Customer", location: "India" },
  { quote: "The Drip Chexk going places, next stop is Sydney. Loving these hoodies.", author: "Verified Customer", location: "Sydney, Australia" },
  { quote: "I love it.", author: "Verified Customer", location: "Dubai" },
];

const Home = () => {
  const featured = PRODUCTS;
  return (
    <>
      <SEO />
      <HeroAnimation />

      {/* Trust strip */}
      <section className="border-b border-[#222] bg-black">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { Icon: Truck, t: "FREE SHIPPING", s: "Orders over ₹1,999" },
            { Icon: ShieldCheck, t: "SECURE PAYMENT", s: "UPI · COD" },
            { Icon: RotateCcw, t: "EASY RETURNS", s: "On damaged items" },
            { Icon: Star, t: "5★ RATED", s: "By the family" },
          ].map(({ Icon, t, s }) => (
            <div key={t} className="flex flex-col items-center gap-1">
              <Icon size={18} className="text-[#FF69B4]" />
              <p className="tiny-link">{t}</p>
              <p className="text-xs text-white/40">{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured collection */}
      <section
        id="shop-collection"
        className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28"
      >
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="tiny-link text-white/40">The Collection</p>
            <h2 className="font-display text-4xl sm:text-6xl mt-3 tracking-[0.05em]">
              SHOP THE DROP<span className="text-[#FF69B4]">.</span>
            </h2>
          </div>
          <Link
            to="/collections"
            data-testid="view-all-link"
            className="tiny-link border-b border-white pb-1 hover:text-[#FF69B4] hover:border-[#FF69B4] transition-colors"
          >
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* Manifesto banner */}
      <section className="relative bg-[#0a0a0a] border-y border-[#222] py-24 sm:py-32 overflow-hidden grain">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="relative z-10 text-center max-w-3xl mx-auto px-6"
        >
          <p className="tiny-link text-[#FF69B4]">Manifesto</p>
          <p className="font-display text-3xl sm:text-5xl mt-6 leading-tight tracking-[0.02em]">
            FOR THE ONES BUILDING A LEGACY,
            <br />
            GETTING LOST ON PURPOSE,
            <br />
            AND NEVER EXPLAINING
            <br />
            THEMSELVES TO MAMA.
          </p>
          <Link
            to="/about"
            data-testid="manifesto-link"
            className="mt-10 inline-block tiny-link border-b border-white pb-1 hover:text-[#FF69B4] hover:border-[#FF69B4] transition-colors"
          >
            Read the story →
          </Link>
        </motion.div>
      </section>

      {/* Reviews */}
      <section
        id="reviews"
        className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28 scroll-mt-24"
      >
        <p className="tiny-link text-white/40 text-center">Customer Love</p>
        <h2 className="font-display text-4xl sm:text-6xl text-center mt-3 tracking-[0.05em]">
          THE DRIP SPEAKS<span className="text-[#FF69B4]">.</span>
        </h2>
        <div className="mt-12 flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto no-scrollbar md:overflow-visible snap-x snap-mandatory pb-2">
          {REVIEWS.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              data-testid={`review-${i}`}
              className="snap-center flex-shrink-0 w-[80%] md:w-auto bg-[#111] border border-[#222] hover:border-[#FF69B4]/50 transition-colors p-6 relative"
            >
              <div className="flex gap-0.5 mb-3 text-[#FF69B4]">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} size={12} fill="#FF69B4" stroke="#FF69B4" />
                ))}
              </div>
              <p className="text-sm leading-relaxed text-white/85">"{r.quote}"</p>
              <div className="mt-5 pt-5 border-t border-[#222]">
                <p className="text-sm font-medium">{r.author}</p>
                <p className="text-xs text-white/40">{r.location}</p>
                <p className="text-[10px] text-[#FF69B4] tracking-[0.28em] uppercase font-medium mt-2">
                  ✓ Verified Buyer
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <NewsletterSection />
    </>
  );
};

const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("You're in. Welcome to the family.");
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <section className="bg-[#0a0a0a] border-y border-[#222] py-20">
      <div className="max-w-2xl mx-auto px-5 text-center">
        <p className="tiny-link text-[#FF69B4]">Newsletter</p>
        <h2 className="font-display text-4xl sm:text-5xl mt-3 tracking-[0.05em]">
          GET THE NEXT DROP FIRST<span className="text-[#FF69B4]">.</span>
        </h2>
        <p className="text-white/50 text-sm mt-4">Early access, restocks, behind the scenes.</p>
        <form onSubmit={submit} className="mt-8 flex border border-[#222] focus-within:border-[#FF69B4] transition-colors">
          <input
            data-testid="newsletter-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 bg-transparent px-4 py-4 text-sm focus:outline-none"
          />
          <button
            data-testid="newsletter-submit"
            disabled={loading}
            className="pink-cta tiny-link px-6 disabled:opacity-50"
          >
            {loading ? "..." : "Subscribe"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Home;
